import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import { Autocomplete, IconButton, TextField } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useStyles } from './style'
import MaskedInput from 'react-text-mask'
import { createNumberMask } from 'text-mask-addons'
import ButtonCustom from 'components/common/ButtonCustom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useStore } from 'hooks/useStore'
import { increaseCounter } from 'stores/features/cart/cart'
import { setSnackbar } from 'stores/features/snackbar/snackbar'
import { addToCart } from 'helper/orderHelper'
import LoaderDots from 'components/common/LoaderDots'
import DateFnsUtils from '@date-io/date-fns'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import { endOfDay, isBefore, startOfDay } from 'date-fns'
import DialogAlert from 'components/common/DialogAlert'

const glAccountSchema = yup.object({ label: yup.string(), value: yup.number() })

const validationSchema = yup.object({
  glAccounts: yup.array(glAccountSchema).min(1, 'Select at least 1 G/L Account'),
  poReason: yup.string().optional(),
  note: yup.string().optional(),
  quantity: yup.number().min(1, "Quantity can't less than 1"),
  expect_date: yup.date().required()
})

const defaultMaskOptions = {
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: '.',
  allowDecimal: false,
  decimalSymbol: ',',
  decimalLimit: 2,
  integerLimit: 7,
  allowNegative: false,
  allowLeadingZeroes: false
}

const today = startOfDay(new Date())

const CurrencyInput = ({ maskOptions, ...inputProps }) => {
  const currencyMask = createNumberMask({ ...defaultMaskOptions, ...maskOptions })

  return <MaskedInput mask={currencyMask} {...inputProps} />
}

const AddToCartForm = ({ data, unit, ids, t }) => {
  const classes = useStyles()

  const [loading, setLoading] = React.useState(false)
  const [isDateError, setDateError] = React.useState(false)
  const [openAlert, setOpenAlert] = React.useState(false)
  const [alertMsg, setAlertMsg] = React.useState('')

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      glAccounts: [],
      note: '',
      poReason: '',
      quantity: 1,
      expect_date: null
    }
  })

  const dispatch = useDispatch()
  const history = useHistory()
  const { landingPage } = useStore()

  const { control, setValue, watch } = form

  let glAccountStorage = []
  try {
    glAccountStorage = JSON.parse(localStorage.getItem('glAccount'))
  } catch (error) {
    console.debug('Failed to parse glAccount')
  }

  const expectedDate = watch('expect_date')
  const glAccounts = watch('glAccounts')
  const poReason = watch('poReason')
  const quantity = watch('quantity')

  const handleAddToCart = async ({ glAccounts, poReason, note, quantity, expect_date }) => {
    const userId = JSON.parse(localStorage.getItem('userid'))

    if (!data.expired_date || isBefore(data.expired_date, today)) {
      setOpenAlert(true)
      setAlertMsg('This material price out of quotation date, please contact administrator')
      return
    }

    if (data.minimum_order && quantity < data.minimum_order) {
      setOpenAlert(true)
      setAlertMsg(`Minimum Order Qty is ${data.minimum_order}`)
      return
    }

    if (userId) {
      if (quantity.length === 0) {
        dispatch(setSnackbar({ show: true, message: 'Quantity must be more than 1' }))
        return
      }
      setLoading(true)

      const input = [
        {
          product_id: parseInt(ids.slice(15)),
          product_variant_id: null,
          quantity,
          note,
          po_reason: poReason,
          general_ledger_account_id: glAccounts.map(item => item.value),
          expect_date: expect_date !== null ? endOfDay(expect_date).toISOString() : undefined
        }
      ]

      try {
        const response = await addToCart(
          input.filter(item => item.quantity > 0),
          landingPage,
          data
        )
        if (response.success) {
          dispatch(increaseCounter())

          setLoading(false)
          dispatch(
            setSnackbar({
              show: true,
              message: t('success_add_to_cart'),
              action: { text: t('show_cart'), onClick: () => history.push('/carts') }
            })
          )
        } else {
          let errorMsg = response.message

          if (response.data.includes('idx_order_cart_items_unique')) {
            errorMsg = 'ERROR: Product already in cart'
          }

          if (response.data.includes('is less than minimum order')) {
            errorMsg = response.data
          }

          dispatch(setSnackbar({ show: true, message: errorMsg }))
        }
      } catch (error) {
        let errorMsg = error.message
        if (error.data?.includes('idx_order_cart_items_unique')) {
          errorMsg = 'ERROR: Product already in cart'
        }
        dispatch(setSnackbar({ show: true, message: errorMsg }))
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (data?.minimum_order) {
      setValue('quantity', data.minimum_order)
    }
  }, [data])

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleAddToCart)}>
          <div className={classes.mainItem}>
            <p className={classes.mainItemTitle}>
              G/L Account <span>*</span>
            </p>
            <p className={classes.mainItemSpacer}>:</p>
            <div style={{ width: '50%' }} className={classes.glAccountAutocomplete}>
              <Controller
                control={control}
                name='glAccounts'
                render={({ field }) => (
                  <Autocomplete
                    size='small'
                    id='checkboxes-tags-demo'
                    options={glAccountStorage.map(gl => ({ value: gl.id, label: gl.name }))}
                    onChange={(_e, _v, _r, option) => {
                      field.onChange([option.option])
                    }}
                    getOptionLabel={option => option.label}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props
                      return (
                        <li key={key} {...optionProps}>
                          {option.label}
                        </li>
                      )
                    }}
                    style={{ width: '100%' }}
                    isOptionEqualToValue={(option, val) => option.value === val.value}
                    renderInput={params => (
                      <TextField {...params} inputP variant='outlined' placeholder='Favorites' />
                    )}
                  />
                )}
              />
            </div>
          </div>
          <div className={classes.mainItem}>
            <p className={classes.mainItemTitle}>
              PO Reason <span>*</span>
            </p>
            <p className={classes.mainItemSpacer}>:</p>
            <Controller
              control={control}
              name='poReason'
              render={({ field }) => (
                <input
                  type='text'
                  className={classes.mainItemInput}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className={classes.mainItem}>
            <p className={classes.mainItemTitle}>Notes</p>
            <p className={classes.mainItemSpacer}>:</p>
            <Controller
              control={control}
              name='note'
              render={({ field }) => (
                <input
                  type='text'
                  className={classes.mainItemInput}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className={classes.mainItem}>
            <p className={classes.mainItemTitle}>
              Expected Delivery Date <span>*</span>
            </p>
            <p className={classes.mainItemSpacer}>:</p>
            <Controller
              control={control}
              name='expect_date'
              render={({ field }) => (
                <KeyboardDatePicker
                  autoOk
                  disablePast
                  disableToolbar
                  variant='inline'
                  format='dd/MM/yyyy'
                  value={field.value}
                  onChange={field.onChange}
                  className={classes.mainItemInput}
                  onError={error => setDateError(!!error)}
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  style={{
                    marginBottom: isDateError ? 10 : 0,
                    paddingRight: 0
                  }}
                />
              )}
            />
          </div>
          <div className={classes.mainItem} style={{ marginTop: 10 }}>
            <p className={classes.mainItemTitle}>Order Qty</p>
            <p className={classes.mainItemSpacer}>:</p>
            <div className={classes.mainItemOrder}>
              <Controller
                control={control}
                name='quantity'
                render={({ field }) => {
                  return (
                    <div className={classes.mainQuantityControl}>
                      <IconButton
                        disabled={data.stock_status === 'Sold Out' || field.value === 1}
                        onClick={() => {
                          if (field.value > 1) {
                            field.onChange(field.value - 1)
                          }
                        }}
                        style={{ padding: 0, width: '25px', height: '25px' }}
                      >
                        <RemoveIcon
                          fontSize='small'
                          style={{ width: '15px', height: '15px', fill: 'black' }}
                        />
                      </IconButton>
                      <CurrencyInput
                        type='text'
                        style={{
                          width: '70px',
                          border: 'none',
                          textAlign: 'center',
                          outline: 'none'
                        }}
                        disabled={data.stock_status === 'Sold Out'}
                        value={String(field.value)}
                        onChange={e => {
                          const newValue = e.target.value.replace(/\D/g, '')
                          field.onChange(newValue ? parseInt(newValue) : '')
                        }}
                      />
                      <IconButton
                        disabled={data.stock_status === 'Sold Out'}
                        onClick={() => {
                          field.onChange(field.value + 1)
                        }}
                        style={{ padding: 0, width: '25px', height: '25px' }}
                      >
                        <AddIcon
                          fontSize='small'
                          style={{ width: '15px', height: '15px', fill: 'black' }}
                        />
                      </IconButton>
                    </div>
                  )
                }}
              />
              {unit?.name && <span style={{ marginLeft: -12 }}>{unit.name}</span>}
              <ButtonCustom
                bg='#000000'
                type='submit'
                disabled={
                  loading ||
                  data.stock_status === 'Sold Out' ||
                  !data ||
                  !glAccounts.length ||
                  !poReason ||
                  !expectedDate ||
                  !quantity
                }
                className={classes.cartButton}
              >
                <svg
                  width={24}
                  height={24}
                  style={{ fill: 'transparent', fontSize: 24, width: 24, margin: '1px !important' }}
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M2 2H3.74001C4.82001 2 5.67 2.93 5.58 4L4.75 13.96C4.61 15.59 5.89999 16.99 7.53999 16.99H18.19C19.63 16.99 20.89 15.81 21 14.38L21.54 6.88C21.66 5.22 20.4 3.87 18.73 3.87H5.82001'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M16.25 22C16.9404 22 17.5 21.4404 17.5 20.75C17.5 20.0596 16.9404 19.5 16.25 19.5C15.5596 19.5 15 20.0596 15 20.75C15 21.4404 15.5596 22 16.25 22Z'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M8.25 22C8.94036 22 9.5 21.4404 9.5 20.75C9.5 20.0596 8.94036 19.5 8.25 19.5C7.55964 19.5 7 20.0596 7 20.75C7 21.4404 7.55964 22 8.25 22Z'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M9 8H21'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                {loading ? <LoaderDots /> : <span style={{ marginLeft: 8 }}>Order</span>}
              </ButtonCustom>
            </div>
          </div>
        </form>
      </FormProvider>
      <DialogAlert open={openAlert} ok='true' title='Info' close={() => setOpenAlert(false)}>
        {alertMsg}
      </DialogAlert>
    </MuiPickersUtilsProvider>
  )
}

export default AddToCartForm
